// One place that turns an RBAC role id into something a human reads.
//
// There were two ways to render a role and they disagreed. The dropdowns used
// `t('screens.users.role.<code>')`, which is translated and — for the three
// roles named after a configurable object — interpolates the tenant's own
// noun. The Team Management table did this instead:
//
//     user.role.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
//
// a runtime transform of the raw enum. It is not translated, so a French or
// German interface still read "Claims Manager"; and it cannot see tenant
// vocabulary, so MOK's roster showed "Claims Manager", "Broker" and "Risk
// Carrier" for a tenant whose objects are Cases, Sponsors and Providers —
// three rows above a dropdown offering Case Manager, Sponsor and Provider.
//
// The bundle keys carry the vocabulary variables, so a role semantically tied
// to a renameable object follows the tenant automatically:
//
//     screens.users.role.claimsManager  =  "{{claim}} Manager"
//     screens.users.role.broker         =  "{{broker}}"
//     screens.users.role.riskCarrier    =  "{{riskCarrier}}"
//
// Roles not tied to a configurable object (adjuster, complianceOfficer,
// orgAdmin…) are ordinary translated strings and are unaffected by vocabulary.
//
// The RBAC identifiers themselves never change — this is presentation only.

/**
 * Humanise an unknown role code as a last resort.
 *
 * Reached only when a role exists in the User enum but has no bundle key yet.
 * Kept so a new role degrades to something readable rather than to a blank
 * cell, but it is a fallback, never the primary path: anything that reaches it
 * is missing a translation and should get one.
 */
const humanise = (code) => String(code || '')
  .replace(/([A-Z])/g, ' $1')
  .replace(/^./, s => s.toUpperCase())
  .trim();

/**
 * Resolve a role id to its display label.
 *
 * @param {Function} t     i18next `t` from useTranslation()
 * @param {string}   code  RBAC role id, e.g. 'claimsManager'
 * @returns {string}
 */
export function roleLabel(t, code) {
  if (!code) return '—';
  // defaultValue keeps an unknown role readable; the vocabulary variables in
  // the bundle strings are interpolated by i18next's defaultVariables, so no
  // caller has to know a vocabulary system exists.
  return t(`screens.users.role.${code}`, { defaultValue: humanise(code) });
}

export default roleLabel;
